import React, { useCallback, useEffect, useState } from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import client, { formatApiError } from "@/lib/api";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const RESOLUTIONS = [
  { key: "correct_component_provided", label: "Correct Component Provided" },
  { key: "component_replaced",         label: "Component Replaced" },
  { key: "order_component_updated",    label: "Order Component Updated" },
  { key: "approved_alternative",       label: "Approved Alternative Component" },
  { key: "other",                      label: "Other" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function ResolveModal({ open, issue, onClose, onResolved }) {
  const [type, setType] = useState("correct_component_provided");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setType("correct_component_provided"); setNotes(""); setBusy(false); }
  }, [open]);

  if (!open || !issue) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/component-issues/${issue.id}/resolve`, {
        resolution_type: type,
        resolution_notes: notes.trim() || null,
      });
      toast.success("Issue resolved. Technician must re-verify component.");
      onResolved && onResolved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resolve failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }} onClick={onClose} data-testid="resolve-issue-modal">
      <div className="rounded-xl border max-w-md w-full max-h-[92vh] overflow-y-auto" style={{ background: "#0d0d10", borderColor: "rgba(0,229,255,0.35)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center gap-2.5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <CheckCircle2 size={16} style={{ color: "#00e5ff" }} />
          <div className="text-white text-sm font-semibold tracking-[0.22em] uppercase">Resolve Issue</div>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold">Component</div>
            <div className="mt-1 text-white text-sm font-semibold">{issue.component_label}</div>
            <div className="text-white/60 text-sm">{issue.expected_value}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-2">Resolution</div>
            <div className="grid grid-cols-1 gap-1.5">
              {RESOLUTIONS.map((r) => (
                <label key={r.key} className="flex items-center gap-2.5 p-2.5 rounded-md cursor-pointer border transition-colors" style={{
                  borderColor: type === r.key ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.06)",
                  background:  type === r.key ? "rgba(0,229,255,0.08)" : "transparent",
                }} data-testid={`resolution-${r.key}`}>
                  <input type="radio" name="resolution-type" checked={type === r.key} onChange={() => setType(r.key)} className="accent-cyan-400" />
                  <span className="text-white text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-1.5">Supervisor Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={500} placeholder="Optional notes for the audit trail." className="w-full rounded-md px-3 py-2 text-sm text-white outline-none" style={{ background: "#050507", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="resolution-notes-input"/>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-end gap-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold tracking-[0.18em] uppercase text-white/60 hover:text-white" data-testid="resolve-cancel">Cancel</button>
          <button type="button" disabled={busy} onClick={submit} className="px-4 py-2 rounded-md text-xs font-semibold tracking-[0.18em] uppercase disabled:opacity-30" style={{ background: "rgba(0,229,255,0.9)", color: "#00252d" }} data-testid="resolve-submit">
            {busy ? "Resolving…" : "Mark as Resolved"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComponentIssuesPage() {
  const [tab, setTab] = useState("open"); // open | resolved
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/admin/component-issues`, { params: { status: tab === "open" ? "OPEN" : "RESOLVED", limit: 200 } });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load issues");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6">
          <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-white/50">Build</div>
          <h1 className="mt-1 text-2xl font-semibold text-white flex items-center gap-2">
            <AlertTriangle size={20} style={{ color: "#ff9800" }} /> Component Issues
          </h1>
        </div>

        <div className="mb-4 inline-flex rounded-md border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          {[{k:"open",l:"Open"},{k:"resolved",l:"Resolved"}].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className="px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
              style={{
                background: tab === t.k ? "rgba(0,229,255,0.10)" : "transparent",
                color:      tab === t.k ? "var(--accent)" : "rgba(255,255,255,0.55)",
              }}
              data-testid={`tab-${t.k}`}
            >{t.l}</button>
          ))}
        </div>

        {loading && <div className="text-white/40 py-6" data-testid="issues-loading">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border p-10 text-center text-white/45" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid="issues-empty">
            {tab === "open" ? "No open component issues. 🎉" : "No resolved issues yet."}
          </div>
        )}

        <div className="space-y-3" data-testid="issues-list">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border p-4 md:p-5" style={{ borderColor: r.status === "OPEN" ? "rgba(255,152,0,0.28)" : "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid={`issue-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md" style={{
                      background: r.status === "OPEN" ? "rgba(255,152,0,0.15)" : "rgba(0,255,136,0.12)",
                      color: r.status === "OPEN" ? "#ffb857" : "#00ff88",
                    }}>{r.status}</div>
                    <div className="text-white/80 text-sm font-semibold">{r.reason_label}</div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <Field label="Order" value={<><span className="text-white/50">#</span>{r.order_number}</>} />
                    <Field label="Station" value={r.station_id || "—"} />
                    <Field label="Technician" value={r.reported_by_name || "—"} />
                    <Field label="Reported" value={fmtDate(r.reported_at)} />
                    <Field label="Component" value={r.component_label} className="col-span-2" />
                    <Field label="Expected" value={r.expected_value || "—"} className="col-span-2" />
                  </div>
                  {r.notes && (
                    <div className="mt-3 rounded-md px-3 py-2 text-sm text-white/80 italic" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      "{r.notes}"
                    </div>
                  )}
                  {r.status === "RESOLVED" && (
                    <div className="mt-3 rounded-md px-3 py-2 text-sm" style={{ background: "rgba(0,255,136,0.05)", border: "1px solid rgba(0,255,136,0.18)" }}>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold">Resolution</div>
                      <div className="text-white mt-0.5">{r.resolution_type_label || "—"}</div>
                      {r.resolution_notes && <div className="text-white/70 italic mt-1">"{r.resolution_notes}"</div>}
                      <div className="text-white/45 text-[11px] mt-1">Resolved by {r.resolved_by_name || "supervisor"} · {fmtDate(r.resolved_at)}</div>
                    </div>
                  )}
                </div>
                {r.status === "OPEN" && (
                  <button
                    type="button"
                    onClick={() => setIssue(r)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md"
                    style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)", border: "1px solid rgba(0,229,255,0.30)" }}
                    data-testid={`resolve-btn-${r.id}`}
                  >
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <ResolveModal open={!!issue} issue={issue} onClose={() => setIssue(null)} onResolved={load} />
      </div>
    </SupervisorLayout>
  );
}

function Field({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold">{label}</div>
      <div className="text-white text-sm mt-0.5">{value}</div>
    </div>
  );
}
